import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Image } from "react-bootstrap";
import { Link } from "react-router-dom";

export interface MobileActionItem {
  label: string;
  icon?: string;
  onClick?: React.MouseEventHandler;
  to?: string;
  disabled?: boolean;
}

const MobileActionsMenu = ({ actions }: { actions: MobileActionItem[] }) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggle = useCallback(() => setOpen((prev) => !prev), []);

  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [open]);

  return (
    <div className="mobile-actions-menu d-lg-none" ref={menuRef}>
      <Button
        variant="outline-secondary"
        size="sm"
        className="mobile-actions-toggle"
        onClick={toggle}
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          fill="currentColor"
          viewBox="0 0 16 16"
        >
          <path d="M9.5 13a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0zm0-5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
        </svg>
        <span className="ms-1">Actions</span>
      </Button>

      {open && (
        <div className="mobile-actions-dropdown" role="menu">
          {actions.map((action) => {
            const content = (
              <>
                {action.icon && (
                  <Image
                    src={action.icon}
                    alt=""
                    width={18}
                    height={18}
                    className="mobile-action-icon"
                  />
                )}
                <span>{action.label}</span>
              </>
            );

            if (action.to && !action.disabled) {
              return (
                <Link
                  key={action.label}
                  to={action.to}
                  className="mobile-action-item"
                  role="menuitem"
                  onClick={() => setOpen(false)}
                >
                  {content}
                </Link>
              );
            }

            return (
              <button
                key={action.label}
                className={`mobile-action-item${action.disabled ? " disabled" : ""}`}
                role="menuitem"
                disabled={action.disabled}
                aria-disabled={action.disabled}
                onClick={(e) => {
                  if (action.disabled) return;
                  setOpen(false);
                  action.onClick?.(e);
                }}
              >
                {content}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MobileActionsMenu;
